import ConfirmDialogVue from '@stl/components/ConfirmDialog.vue';
import { createApp, inject, type Plugin, type App } from 'vue';
import { createBootstrap } from 'bootstrap-vue-next';

const confirmKey = 'confirm';
interface ConfirmOptions {
  title?: string;
  html?: boolean;
  yesText?: string;
  noText?: string;
  yesVariant?: string;
  noVariant?: string;
  autoClose?: boolean;
  hard?: string;
}

export interface ConfirmDialog {
  // eslint-disable-next-line no-unused-vars
  result: boolean;
  close: (ok: boolean) => void;
}

export interface ConfirmPlugin {
  show: (question: string, options?: ConfirmOptions) => ConfirmPromise;
}

export class ConfirmPromise {
  private promise: Promise<ConfirmDialog>;
  private app: App<Element> | null;
  constructor(promise: Promise<ConfirmDialog>, app: App<Element> | null) {
    this.promise = promise;
    this.app = app;
  }

  then<TResult1 = ConfirmDialog, TResult2 = never>(
    onfulfilled?: ((value: ConfirmDialog) => TResult1 | PromiseLike<TResult1>) | undefined | null,
    onrejected?: ((reason: any) => TResult2 | PromiseLike<TResult2>) | undefined | null
  ): Promise<TResult1 | TResult2> {
    return this.promise.then(onfulfilled, onrejected);
  }

  catch<TResult = never>(
    onrejected?: ((reason: any) => TResult | PromiseLike<TResult>) | undefined | null
  ): Promise<ConfirmDialog | TResult> {
    return this.promise.catch(onrejected);
  }

  close() {
    if (this.app) {
      this.app.unmount();
    }
  }
}

const plugin = () => {
  return {
    show: (question: string, options?: ConfirmOptions) => {
      let app: App<Element> | null = null;
      const promise = new Promise<ConfirmDialog>((resolve) => {
        if (!options) {
          options = {};
        }
        app = createApp(ConfirmDialogVue, {
          resolve: resolve,
          text: question,
          ...options
        }).use(createBootstrap());
        const wrapper = document.createElement('div');
        app.mount(wrapper);
        document.body.appendChild(wrapper);
      });

      return new ConfirmPromise(promise, app);
    }
  };
};

const VueConfirmPlugin: Plugin = (App) => {
  const inter = plugin();
  App.provide(confirmKey, inter);
};

const useConfirm = (customKey = ''): ConfirmPlugin => {
  return inject<any>(customKey !== '' ? customKey : confirmKey);
};

export default VueConfirmPlugin;

export { useConfirm, type ConfirmOptions };
